import { DeckMappDialog } from 'components/DeckMappDialog'
import React, { useState, useMemo } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Button,
  Divider
} from '@mui/material'
import * as XLSX from 'xlsx'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'

export const ExportWearData = ({ histories, open, onClose }) => {
  const [isExporting, setIsExporting] = useState(false)
  const [selectedHistories, setSelectedHistories] = useState([])
  const { database, deckId } = useCurrentDeckContext()

  const deckRevisions = useMemo(() => {
    return database.deckRevisions.filter((dr) => dr.DeckId === deckId)
  }, [database.deckRevisions, deckId])

  const historyList = useMemo(() => histories || [], [histories])

  const handleToggle = (historyId) => {
    setSelectedHistories((prev) => {
      if (prev.includes(historyId)) {
        return prev.filter((id) => id !== historyId)
      } else {
        return [...prev, historyId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedHistories.length === historyList.length) {
      setSelectedHistories([])
    } else {
      setSelectedHistories(historyList.map((h) => h.id))
    }
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const workbook = XLSX.utils.book_new()

      for (let i = 0; i < selectedHistories.length; i++) {
        const historyId = selectedHistories[i]
        const history = historyList.find((h) => h.id === historyId)

        if (!history) continue

        const revision = deckRevisions.find((dr) => dr.id === history.RevisionId)

        if (!revision?.Size) continue

        const rows = revision.Size.Rows
        const cols = revision.Size.Columns

        const worksheetData = []
        for (let row = 0; row < rows; row++) {
          const rowData = []
          for (let col = 0; col < cols; col++) {
            const detail = history.Details.find(
              (d) =>
                d.Panel &&
                d.Panel.Position &&
                d.Panel.Position.Row === row &&
                d.Panel.Position.Column === col &&
                d.WearAppResponse === 'add-wear-action'
            )

            if (detail) {
              rowData.push(detail.WorkingDepth)
            } else {
              rowData.push('')
            }
          }
          worksheetData.push(rowData)
        }

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

        const columnWidth = Math.max(10, Math.floor(100 / cols))
        worksheet['!cols'] = Array(cols).fill({ wch: columnWidth })

        const sheetName = (history.ShortDescription || `History ${i + 1}`)
          .substring(0, 31)
          .replace(/[:\\/?*\[\]]/g, '_')

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      }

      const deckName = database.decks.find((d) => d.id === deckId)?.Name

      const fileName = `wear_data_export_${deckName || ''}_${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(workbook, fileName)

      onClose()
    } catch {
      // do nothing
    } finally {
      setIsExporting(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    } catch {
      return dateString
    }
  }

  return (
    <DeckMappDialog
      isLoading={isExporting}
      saveButtonProps={{
        title: 'Export',
        onClick: handleExport,
        disabled: selectedHistories.length === 0
      }}
      open={open}
      onCancel={onClose}
      title="Export Wear Data">
      <Box sx={{ width: '100%', minHeight: 400, maxHeight: 600 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedHistories.length} of {historyList.length} selected
          </Typography>
          <Button variant="text" size="small" onClick={handleSelectAll}>
            {selectedHistories.length === historyList.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        <List sx={{ width: '100%', bgcolor: 'background.paper', overflow: 'auto', maxHeight: 500 }}>
          {historyList.map((history, index) => {
            const historyId = history.Id || history.id
            const isSelected = selectedHistories.includes(historyId)

            return (
              <React.Fragment key={historyId || index}>
                <ListItem disablePadding>
                  <ListItemButton role={undefined} onClick={() => handleToggle(historyId)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': `checkbox-list-label-${historyId}` }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={`checkbox-list-label-${historyId}`}
                      primary={
                        <Box>
                          <Typography variant="body1" component="span" sx={{ fontWeight: 500 }}>
                            {history.ShortDescription || 'No description'}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" component="span" color="text.secondary">
                            Created by: {history.CreatedBy || 'Unknown'}
                          </Typography>
                          <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 2 }}>
                            Date: {formatDate(history.DatePerformed)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < historyList.length - 1 && <Divider />}
              </React.Fragment>
            )
          })}
        </List>
      </Box>
    </DeckMappDialog>
  )
}
